// @flow
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import moment from "moment";
import "moment/locale/sv";
import "moment/locale/en-gb";
import "moment/locale/fi";
import "moment/locale/nb";
import { getProjects, searchOrderById, searchOrderByPhone, activateTicket, getTicketCount, getProductDeparturesCount } from "../../../../utils/api/restClient";
import { translatePage } from "../../../../utils/services/translate";
import { LoadingComponent } from "../../../../components/layout/loading/LoadingComponent";

// Actions
import { setSelectedProject, setProjects, setProjectsError } from "../../../../redux/project/actions";
import { setExpandedOrder, setOrdersError, setOrders } from "../../../../redux/order/actions";
import { setLoading } from "../../../../redux/actions";

const toMomentLocale = (language) => {
  if (!language) return "sv";
  return language.split("-")[0];
};

const formatDate = (date, locale, format = "YYYY-MM-DD HH:mm", preserveTimezone = false) => {
  if (!date) return "-";
  const m = (preserveTimezone ? moment.parseZone(date) : moment(date)).locale(toMomentLocale(locale));
  return m.isValid() ? m.format(format) : "-";
};

const getDeparturesCount = (products = []) => {
  if (!Array.isArray(products)) return 0;

  return products.reduce((sum, product) => {
    const departures = Array.isArray(product?.departures) ? product.departures : [];

    if (departures.length > 0) {
      return sum + departures.reduce((departuresSum, departure) => departuresSum + (departure?.validTicketCount || 0), 0);
    }

    return sum + (product?.ticketCount || 0);
  }, 0);
};

const getTicketStatusText = (status) => {
  switch (status.toLowerCase()) {
    case 'valid':
      return 'Giltig';
    case 'activated':
      return 'Aktiverad';
    case 'used':
      return 'Validerad';
    case 'refunded':
      return 'Återbetald';
    case 'expired':
      return 'Utgått';
    case 'pendingrefund':
      return 'Återbetalning pågår';
    case 'refundfailed':
      return 'Misslyckades återbetala';
    case 'deleted':
      return 'Biljett borttagen';
    default:
      return status;
  }
};

function SearchPage() {
  const dispatch = useDispatch();
  const { projects, selectedProject, error: projectError } = useSelector(state => state.Project);
  const { orders, expandedOrderId, error: orderError } = useSelector(state => state.Order);
  const { loading, language, translations } = useSelector(state => state.Layout);

  const [searchType, setSearchType] = useState("orderId");
  const [searchValue, setSearchValue] = useState("");
  const [validationError, setValidationError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [ticketCountData, setTicketCountData] = useState(null);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [departuresData, setDeparturesData] = useState(null);
  const [showDeparturesDetails, setShowDeparturesDetails] = useState(false);

  const departuresCount = useMemo(() => getDeparturesCount(departuresData?.products), [departuresData]);

  const loadProjects = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const projectsData = await getProjects();
      dispatch(setProjects(projectsData));
      setProjectsLoaded(true);
    } catch (err) {
      dispatch(setProjectsError(translatePage(translations, language, "adminSearchPage", "projectLoadError")));
      setProjectsLoaded(true);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, translations, language]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!selectedProject) {
      dispatch(setOrdersError(translatePage(translations, language, "adminSearchPage", "selectEventFirst")));
      return;
    }

    if (searchType === "phone") {
      if (!phoneNumber || !isValidPhone) {
        setValidationError(translatePage(translations, language, "adminSearchPage", "enterValidPhone"));
        return;
      }
    } else {
      if (!searchValue.trim()) {
        setValidationError(translatePage(translations, language, "adminSearchPage", "enterSearchValue"));
        return;
      }
    }

    // Clear state before new search
    setValidationError("");
    dispatch(setLoading(true));
    dispatch(setOrdersError(null));
    dispatch(setOrders([]));
    dispatch(setExpandedOrder(null));

    try {
      let results;
      if (searchType === "orderId") {
        results = await searchOrderById(selectedProject, searchValue.trim());
        if (!results) {
          dispatch(setOrdersError(translatePage(translations, language, "adminSearchPage", "noOrdersFound")));
          return;
        }
        results = Array.isArray(results) ? results : [results];
      } else {
        results = await searchOrderByPhone(selectedProject, phoneNumber);
      }
      
      if (results && Array.isArray(results) && results.length > 0) {
        dispatch(setOrders(results));
        dispatch(setOrdersError(null));
      } else {
        dispatch(setOrdersError(translatePage(translations, language, "adminSearchPage", "noOrdersFound")));
      }
    } catch (error) {
      dispatch(setOrdersError(translatePage(translations, language, "adminSearchPage", "searchError")));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleExpandOrder = async (orderId) => {
    if (orderId === null) {
      dispatch(setExpandedOrder(null));
      return;
    }

    try {
      dispatch(setLoading(true));
      dispatch(setExpandedOrder(orderId));
      
      // Find the order in current state
      const currentOrder = orders.find(order => order.id === orderId);
      
      // If order exists but has no tickets, fetch it again
      if (currentOrder && !currentOrder.tickets) {
        const orderDetails = await searchOrderById(selectedProject, orderId);
        if (orderDetails) {
          // Update only this order in the orders array
          const updatedOrders = orders.map(order => 
            order.id === orderId ? orderDetails : order
          );
          dispatch(setOrders(updatedOrders));
        }
      }
    } catch (error) {
      dispatch(setOrdersError(translatePage(translations, language, "adminSearchPage", "orderDetailsError")));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleActivateTicket = async (orderId, itemId) => {
    try {
      dispatch(setLoading(true));
      await activateTicket(itemId);
      const updatedOrder = await searchOrderById(selectedProject, orderId);
      if (updatedOrder) {
        // Update only this order in the orders array
        const updatedOrders = orders.map(order => 
          order.id === orderId ? updatedOrder : order
        );
        dispatch(setOrders(updatedOrders));
      }
    } catch (error) {
      dispatch(setOrdersError(translatePage(translations, language, "adminSearchPage", "activateTicketError")));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="adminview container mt-4 pt-5" style={{ maxWidth: "450px", height: "100%" }}>
      {!projectsLoaded ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: "100vh" }}>
          <LoadingComponent />
          <p className="mt-3">{translatePage(translations, language, "adminSearchPage", "loadingEvents")}</p>
        </div>
      ) : projectError || !projects || projects.length === 0 ? (
        <div className="card mt-5">
          <div className="card-header">
            <h4 className="mb-0">{translatePage(translations, language, "adminSearchPage", "searchTicketsOrders")}</h4>
          </div>
          <div className="card-body">
            <div className="alert alert-danger text-center">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {projectError || translatePage(translations, language, "adminSearchPage", "noEventsAvailable")}
            </div>
          </div>
        </div>
      ) : (
        <div className="card mt-5">
          <div className="card-header">
            <h4 className="mb-0">{translatePage(translations, language, "adminSearchPage", "searchTicketsOrders")}</h4>
          </div>
          <div className="card-body">
            <form>
              <div className="mb-3">
                <label className="form-label">Event</label>
                <select
                  className="form-select"
                  value={selectedProject || ""}
                  onChange={(e) => {
                    const projectId = e.target.value;
                    dispatch(setSelectedProject(projectId));
                    setSearchValue("");
                    setValidationError("");
                    dispatch(setOrdersError(null));
                    dispatch(setOrders([]));
                    setTicketCountData(null);
                    setShowTicketDetails(false);
                    setDeparturesData(null);
                    setShowDeparturesDetails(false);
                    if (projectId) {
                      getTicketCount(projectId).then((res) => {
                        if (res) setTicketCountData(res);
                      });
                      getProductDeparturesCount(projectId).then((res) => {
                        if (res) setDeparturesData(res);
                      });
                    }
                  }}
                >
                  <option value="">{translatePage(translations, language, "adminSearchPage", "selectProject")}</option>
                  {projects && projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {ticketCountData !== null && (
                  <div className="mt-2 small">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline text-decoration-underline"
                      style={{ fontSize: "inherit" }}
                      onClick={() => setShowTicketDetails(prev => !prev)}
                    >
                      <strong>{translatePage(translations, language, "adminSearchPage", "ticketsSold")}:</strong> {ticketCountData.ticketCount}
                    </button>
                    {showTicketDetails && (
                      <div className="mt-1 text-muted" style={{ paddingLeft: '1rem' }}>
                        {ticketCountData.validTicketCount !== undefined && (
                          <div>{translatePage(translations, language, "adminSearchPage", "validTickets")}: {ticketCountData.validTicketCount}</div>
                        )}
                        {ticketCountData.activatedTicketCount !== undefined && (
                          <div>{translatePage(translations, language, "adminSearchPage", "activatedTickets")}: {ticketCountData.activatedTicketCount}</div>
                        )}
                        {ticketCountData.usedTicketCount !== undefined && (
                          <div>{translatePage(translations, language, "adminSearchPage", "usedTickets")}: {ticketCountData.usedTicketCount}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {departuresData !== null && departuresData.products?.length > 0 && (
                  <div className="mt-2 small">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline text-decoration-underline"
                      style={{ fontSize: "inherit" }}
                      onClick={() => setShowDeparturesDetails(prev => !prev)}
                    >
                      <strong>{translatePage(translations, language, "adminSearchPage", "departuresCount")}:</strong> {departuresCount}
                    </button>
                    {showDeparturesDetails && (
                      <div className="mt-1" style={{ paddingLeft: '1rem' }}>
                        {departuresData.products.map((product) => {
                          const departures = Array.isArray(product.departures) ? product.departures : [];

                          return (
                          <React.Fragment key={product.productId || product.productName}>
                            {departures.length > 0
                              ? departures.map((dep) => (
                                  <div key={dep.departureId} className="text-muted">
                                    {product.productName}{dep.destination ? ` (${dep.destination})` : ""} ({formatDate(dep.departureTime, language, "dddd, YYYY-MM-DD HH:mm", true)}): {dep.validTicketCount}
                                  </div>
                                ))
                              : (
                                  <div className="text-muted">
                                    {product.productName}: {product.ticketCount}
                                  </div>
                                )
                            }
                          </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedProject && (
                <>
                  <div className="mb-3">
                    <label className="form-label">{translatePage(translations, language, "adminSearchPage", "searchFor")}</label>
                    <select
                      className="form-select"
                      value={searchType}
                      onChange={(e) => {
                        setSearchType(e.target.value);
                        setSearchValue("");
                        setValidationError("");
                        dispatch(setOrdersError(null));
                      }}
                    >
                      <option value="orderId">{translatePage(translations, language, "adminSearchPage", "orderId")}</option>
                      <option value="phone">{translatePage(translations, language, "adminSearchPage", "phoneNumber")}</option>
                    </select>
                  </div>

                  {searchType === "phone" ? (
                    <div className="mb-3">
                      <label className="form-label">{translatePage(translations, language, "adminSearchPage", "phoneNumber")}</label>
                      <PhoneInput
                        international
                        defaultCountry="SE"
                        value={phoneNumber}
                        onChange={(value) => {
                          setPhoneNumber(value);
                          setIsValidPhone(value && value.length >= 10);
                          setValidationError("");
                          dispatch(setOrdersError(null));
                          dispatch(setOrders([]));
                        }}
                        className="form-control"
                        style={{ display: 'flex', alignItems: 'center' }}
                      />
                      <div className="form-text text-muted">
                        {translatePage(translations, language, "adminSearchPage", "enterValidPhoneWithCountry")}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <label className="form-label">Order ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={searchValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSearchValue(value);
                          if (value && !/^\d+$/.test(value) && !/^ORD\d+$/.test(value)) {
                            setValidationError(translatePage(translations, language, "adminSearchPage", "orderIdFormat"));
                          } else {
                            setValidationError("");
                          }
                        }}
                        placeholder={translatePage(translations, language, "adminSearchPage", "enterOrderId")}
                      />
                      <div className="form-text text-muted">
                        {translatePage(translations, language, "adminSearchPage", "enterOrderIdExample")}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={handleSearch}
                    disabled={loading || (searchType === "phone" ? !isValidPhone : !searchValue.trim())}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {translatePage(translations, language, "adminSearchPage", "searching")}
                      </>
                    ) : (
                      "Sök"
                    )}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      {validationError && (
        <div className="alert alert-warning mt-3">
          {validationError}
        </div>
      )}

      {orderError && (
        <div className="alert alert-danger mt-3">
          {orderError}
        </div>
      )}

      {Array.isArray(orders) && orders.length > 0 && (
        <div className="mt-4">
          <h5>Sökresultat</h5>
          {orders.map((order) => {
            console.log('Order data:', order);
            return (
              <div key={order.id} className="card mb-3 shadow-sm">
                <div
                  className="card-header d-flex justify-content-between align-items-center bg-white border-bottom-0"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleExpandOrder(expandedOrderId === order.id ? null : order.id)}
                >
                  <div>
                    <strong>OrderNr:</strong> {order.id}
                  </div>
                  <div className="text-muted">
                    {order.phoneNumber}
                  </div>
                </div>
                {expandedOrderId === order.id && (
                  <div className="card-body bg-light">
                    {loading && order.id === expandedOrderId ? (
                      <div className="text-center py-3">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{translatePage(translations, language, "adminSearchPage", "loading")}</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="row g-2 mb-3">
                          <div className="col-12">
                           <p className="mb-0 text-m"><strong>{translatePage(translations, language, "adminSearchPage", "phone")}: </strong> {order.phoneNumber}</p>
                            <p className="mb-1 text-m"><strong>{translatePage(translations, language, "adminSearchPage", "purchaseDate")}: </strong> {formatDate(order.paidDateTime, language)}</p>
                            <p className="mb-0 text-m"><strong>{translatePage(translations, language, "adminSearchPage", "numberOfTickets")}: </strong> {order.tickets ? order.tickets.length : 0}</p>
                         
                          </div>
                        </div>
                        <hr />
                        <h6 className="mb-3">{translatePage(translations, language, "adminSearchPage", "tickets")}</h6>
                        {order.tickets && order.tickets.map((ticket) => (
                          <div key={ticket.referenceId} className={`mb-3 p-2 rounded border ${
                            ticket.status.toLowerCase() === 'valid' ? 'bg-white' : 'bg-danger bg-opacity-25'
                          }`}>
                            <div className="mb-2">
                              <strong className="d-block text-muted">{ticket.name}</strong>
                              <small className="text-muted">{ticket.description}</small>
                              <div className="mt-2">
                                <small className="text-muted">
                                  <strong>Status:</strong> {getTicketStatusText(ticket.status)}
                                </small>
                                {ticket.price && (
                                  <div className="text-muted">
                                    <small><strong>{translatePage(translations, language, "adminSearchPage", "price")}: </strong> {ticket.price} {order.currency}</small>
                                  </div>
                                )}
                                {ticket.departureTime && (
                                  <div className="text-muted">
                                    <small><strong>{translatePage(translations, language, "adminSearchPage", "departure")}: </strong> {formatDate(ticket.departureTime, language, "YYYY-MM-DD HH:mm", true)}</small>
                                  </div>
                                )}
                                {ticket.expirationDate && (
                                  <div className="text-muted">
                                    <small><strong>{translatePage(translations, language, "adminSearchPage", "expiryDate")}: </strong> {formatDate(ticket.expirationDate, language)}</small>
                                  </div>
                                )}
                                {ticket.activationDate && (
                                  <div className="text-muted">
                                    <small><strong>{translatePage(translations, language, "adminSearchPage", "activated")}: </strong> {formatDate(ticket.activationDate, language)}</small>
                                  </div>
                                )}
                                {ticket.status.toLowerCase() === 'used' && ticket.validationAttempts && ticket.validationAttempts.length > 0 && (
                                  <div className="text-muted">
                                    <small><strong>{translatePage(translations, language, "adminSearchPage", "validated")}: </strong> {formatDate(ticket.validationAttempts[ticket.validationAttempts.length - 1].verified, language)}</small>
                                  </div>
                                )}
                                {ticket.activatedCountdownSecondsRemaining > 0 && (
                                  <div className="text-muted">
                                    <small><strong>{translatePage(translations, language, "adminSearchPage", "remainingTime")}: </strong> {ticket.activatedCountdownSecondsRemaining} {translatePage(translations, language, "adminSearchPage", "seconds")}</small>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="d-flex justify-content-end">
                              {ticket.status.toLowerCase() === 'valid' ? (
                                <button
                                  className="btn w-100 btn-success"
                                  onClick={() => handleActivateTicket(order.id, ticket.referenceId)}
                                  disabled={loading}
                                >
                                  {translatePage(translations, language, "adminSearchPage", "activate")}
                                </button>
                              ) : (
                                <span className={`badge ${
                                  ticket.status.toLowerCase() === 'activated' ? 'bg-success' :
                                  ticket.status.toLowerCase() === 'used' ? 'bg-info' :
                                  ticket.status.toLowerCase() === 'expired' ? 'bg-warning' :
                                  ticket.status.toLowerCase() === 'refunded' ? 'bg-secondary' :
                                  ticket.status.toLowerCase() === 'pendingrefund' ? 'bg-warning' :
                                  ticket.status.toLowerCase() === 'refundfailed' ? 'bg-danger' :
                                  ticket.status.toLowerCase() === 'deleted' ? 'bg-dark' :
                                  'bg-secondary'
                                }`}>
                                  {getTicketStatusText(ticket.status)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SearchPage;
